import { useEffect, useMemo, useState } from 'react'

type GeneralErrorButtonProps = {
  canReport: boolean
}

export default function GeneralErrorButton({ canReport }: GeneralErrorButtonProps) {
  const [description, setDescription] = useState('')
  const [descriptionInvalid, setDescriptionInvalid] = useState(false)
  const [screenshot, setScreenshot] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const modalId = useMemo(() => `general-error-${Math.random().toString(36).slice(2)}`, [])

  if (!canReport) {
    return null
  }

  const prepareScreenshot = async () => {
    if (!window.html2canvas) return
    const canvas = await window.html2canvas(document.body)
    setScreenshot(canvas.toDataURL())
  }

  const open = async () => {
    window.showLoadingModal?.({ message: 'Preparing Error Report...' })
    try {
      await prepareScreenshot()
    } catch (error) {
      console.error(error)
    } finally {
      window.hideLoadingModal?.()
    }

    setIsOpen(true)
  }

  const submit = async () => {
    if (!description.trim()) {
      setDescriptionInvalid(true)
      return
    }

    const params = new URLSearchParams()
    params.set('description', description.trim())
    params.set('screenshotBase64', screenshot)
    params.set('errorLog', window.globalErrorLog ? JSON.stringify(window.globalErrorLog) : '')

    try {
      const response = await fetch('/api/v2/issues/generic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      })

      if (!response.ok) {
        throw new Error('Request failed')
      }

      window.enqueueToast?.({
        title: 'Sent',
        message: 'Message sent.',
        duration: 2500,
        showOnMobile: true,
      })
      setIsOpen(false)
    } catch (error) {
      console.error(error)
      window.enqueueToast?.({
        theme: 'danger',
        icon: 'fa fa-xmark',
        title: 'Error',
        message: 'An unknown error occurred sending your message.',
        duration: 2500,
        showOnMobile: true,
      })
      setIsOpen(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen])

  return (
    <>
      <div>
        <cer-btn onClick={open} data-style="ghost">
          <i className="fas fa-bug"></i>
          <span className="cer-only-lg">Report Issue</span>
        </cer-btn>
      </div>

      {isOpen && (
        <div
          className="cer-modal-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setIsOpen(false)
            }
          }}
        >
          <div className="cer-modal" role="dialog" aria-modal="true" aria-labelledby={`${modalId}-title`}>
            <div className="cer-modal-header">
              <h5 className="cer-modal-title" id={`${modalId}-title`}>
                Report Issue or Request Help
              </h5>
              <button type="button" className="cer-modal-close" aria-label="Close" onClick={() => setIsOpen(false)}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="cer-modal-body">
              <textarea
                name="description"
                className={`cer-textarea${descriptionInvalid ? ' cer-textarea--invalid' : ''}`}
                placeholder="Please describe your issue with as much detail as possible"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                  if (descriptionInvalid) setDescriptionInvalid(false)
                }}
              ></textarea>
            </div>
            <div className="cer-modal-footer">
              <button type="button" className="cer-button cer-button--primary" onClick={submit}>
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
